import { useState, useEffect } from "react";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { Mail, Check, Search, Loader2 } from "lucide-react";
import { brevoTemplateService } from "../../services/brevoTemplateService";

interface Template {
  id: number;
  name: string;
  subject: string;
  createdAt: string;
  updatedAt: string;
  isActive?: boolean;
}

interface TemplateSelectorProps {
  value: string;
  onChange: (templateId: string) => void;
  disabled?: boolean;
}

export function TemplateSelector({
  value,
  onChange,
  disabled = false,
}: TemplateSelectorProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTemplates = async () => {
      setIsLoading(true);
      try {
        const response = await brevoTemplateService.getTemplates({
          status: "all",
        });
        setTemplates(response.data || []);
      } catch (error) {
        console.error("Failed to load templates:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadTemplates();
  }, []);

  const filteredTemplates = templates.filter((template) => {
    const searchTerm = search.toLowerCase();
    return (
      template.name.toLowerCase().includes(searchTerm) ||
      template.subject?.toLowerCase().includes(searchTerm)
    );
  });

  const selectedTemplate = templates.find(
    (t) => String(t.id) === String(value),
  );

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-[#102040] mb-2">
          Select Brevo Template *
        </label>
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates..."
            disabled={isLoading || disabled}
            className="pl-10"
          />
          {isLoading && (
            <Loader2
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin"
            />
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="text-[#f09040] animate-spin" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Mail size={48} className="text-gray-300 mb-3" />
          <p className="text-sm text-[#989797]">
            {search ? "No templates found" : "No templates available"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto custom-scrollbar">
          {filteredTemplates.map((template) => {
            const isSelected = String(template.id) === String(value);
            return (
              <Card
                key={template.id}
                onClick={() => !disabled && onChange(String(template.id))}
                className={`p-4 cursor-pointer transition-all ${
                  isSelected
                    ? "border-[#f09040] bg-[#FFF9E6] ring-2 ring-[#f09040]"
                    : "border-gray-200 hover:border-[#f09040] hover:bg-gray-50"
                } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#f09040] to-[#e07828] flex items-center justify-center">
                      <Mail size={20} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-[#102040] truncate">
                        {template.name}
                      </h4>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-[#f09040] flex items-center justify-center">
                      <Check size={14} className="text-white" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-[#989797] mb-3 line-clamp-2">
                  {template.name}
                </p>
                <div className="flex items-center justify-between">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      template.isActive !== false
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {template.isActive !== false ? "Active" : "Inactive"}
                  </span>
                  <span className="text-xs text-[#989797]">
                    {new Date(template.createdAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {selectedTemplate && (
        <Card className="p-4 bg-[#FFF9E6] border-[#f09040]">
          <h4 className="text-sm font-semibold text-[#102040] mb-2">
            Selected Template Details
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#989797]">Template:</span>
              <span className="font-medium text-[#102040]">
                {selectedTemplate.name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#989797]">ID:</span>
              <span className="font-medium text-[#102040]">
                {selectedTemplate.id}
              </span>
            </div>
            {selectedTemplate.subject && (
              <div className="flex justify-between">
                <span className="text-[#989797]">Default Subject:</span>
                <span className="font-medium text-[#102040]">
                  {selectedTemplate.subject}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-[#989797]">Status:</span>
              <span
                className={`font-medium ${
                  selectedTemplate.isActive !== false
                    ? "text-green-600"
                    : "text-gray-600"
                }`}
              >
                {selectedTemplate.isActive !== false ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
